'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import parsePhoneNumber from 'libphonenumber-js';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    InputMask,
    InputMaskProps,
    InputMaskChangeEvent
} from 'primereact/inputmask';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {phoneRenderer} from '~/frontend/src/utilities/valueRenderers';
import {StoreState} from '~/frontend/src/store';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';


type InputProps = Omit<InputMaskProps, 'autoComplete' | 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = InputMaskChangeEvent;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * Default country code that will be set automatically if not specified
     */
    defaultCountryCode? : string,

    /**
     * Show a button for linking.
     */
    linkButton? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean,

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    defaultCountryCode: '49',
    linkButton        : true,
    markDirty         : false,
    rawMode           : false,
    inputClassNames   : [],
    inputStyle        : {},
    useHookForm       : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const PhoneField = React.forwardRef<InputMask, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  autoComplete,
                  disabled,
                  id,
                  defaultCountryCode,
                  inputClassNames,
                  inputStyle,
                  linkButton,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  pt,
                  rawMode,
                  tabIndex,
                  useHookForm,
                  value
              }                                 = props,
              elementId                         = id ?? `${uid}_phoneField`,
              inputRef                          = useRef<InputMask | null>( null ),
              inValue                           = useRef<string | undefined>( value ),
              outValue                          = useRef<string | undefined>( value ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              cursorPosition                    = useRef<number>( -1 ),
              [ elementValue, setElementValue ] = useState<string | undefined>( value ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              fieldWrapperAutoCompleteConfig    = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue                  : elementValue,
                              autoCompleteInputHasFocus          : focus,
                              onAutoCompleteValueChange          : ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  const autoCompleteValue = autoCompleteValueRaw.replace( ' ', '' );

                                  if ( autoCompleteValue !== elementValue ) {
                                      setElementValue( autoCompleteValue );

                                      onChange( autoCompleteValue, originalEvent );
                                  }
                              },
                              autoCompleteSuggestionsItemTemplate: ( suggestion : string ) => {
                                  return phoneRenderer( suggestion, undefined, rawMode );
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange
                  ]
              ),
              mask                              = useMemo(
                  () => {
                      let retVal : string;

                      // build mask
                      if ( ( elementValue ?? '' ) === '' ) {
                          retVal = '99999999999999999999';
                      }
                      else if ( !rawMode ) {
                          const parsedValue = parsePhoneNumber( elementValue ?? '' );

                          retVal = parsedValue
                              ? parsedValue.formatInternational().replace( /[0-9]/g, '9' ) + '?99999999999999999999'
                              : `+${'9'.repeat( Math.max( 0, ( elementValue ?? '' ).length - 1 ) )}?99999999999999999999`
                      }
                      else {
                          retVal = `+${'9'.repeat( Math.max( 0, ( elementValue ?? '' ).length - 1 ) )}?99999999999999999999`
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      focus,
                      rawMode
                  ]
              ),
              fixCursorPosition                 = useCallback(
                  () => {
                      const currentValue       = ( inputRef.current as unknown as HTMLInputElement )?.value ?? '',
                            lastValidCharIndex = currentValue.match( /[+ \d]*/ )?.[ 0 ].length,
                            selectionStart     = ( inputRef.current as unknown as HTMLInputElement )?.selectionStart,
                            selectionEnd       = ( inputRef.current as unknown as HTMLInputElement )?.selectionEnd;

                      let selectionStartToSet = selectionStart,
                          selectionEndToSet   = selectionEnd;

                      if ( lastValidCharIndex !== undefined && selectionStartToSet !== null && selectionEndToSet !== null ) {
                          if ( selectionStartToSet > lastValidCharIndex ) {
                              selectionStartToSet = lastValidCharIndex;
                          }

                          if ( selectionEndToSet > lastValidCharIndex ) {
                              selectionEndToSet = lastValidCharIndex;
                          }

                          if ( selectionStartToSet !== selectionStart || selectionEndToSet !== selectionEnd ) {
                              ( inputRef.current as unknown as HTMLInputElement )?.setSelectionRange?.( selectionStartToSet, selectionEndToSet );
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const
                          currentValue = ( inputRef.current as unknown as HTMLInputElement ).value,
                          changedValue = event.value ? `+${event.value.replace( /^0(?!0)(.*)$/, defaultCountryCode + '$1' ).replace( /^00(.*)/, '$1' )}` : undefined;

                      if ( changedValue !== currentValue ) {
                          setElementValue( changedValue );

                          if ( ( !currentValue?.length || currentValue === '+0' || !currentValue?.startsWith( `+${defaultCountryCode}` ) ) && changedValue?.startsWith( `+${defaultCountryCode}` ) ) {
                              ( inputRef.current as unknown as HTMLInputElement )?.setSelectionRange?.( changedValue!.length, changedValue!.length );
                          }

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange             = useCallback(
                  ( event : InputChangeProps ) => {
                      const
                          currentValue = ( inputRef.current as unknown as HTMLInputElement ).value,
                          changedValue = event.value ? `+${event.value.replace( /^0(?!0)(.*)$/, defaultCountryCode + '$1' ).replace( /^00(.*)/, '$1' )}` : undefined;

                      if ( changedValue !== currentValue ) {
                          setElementValue( changedValue );

                          if ( ( !currentValue?.length || !currentValue?.startsWith( `+${defaultCountryCode}` ) ) && changedValue?.startsWith( `+${defaultCountryCode}` ) ) {
                              ( inputRef.current as unknown as HTMLInputElement )?.setSelectionRange?.( changedValue!.length, changedValue!.length );
                          }

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onFieldFocus                      = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.add( 'p-focus' );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onFieldBlur                       = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.remove( 'p-focus' );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( true );

                      onFieldFocus();
                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current === '+' ) {
                          onChange( undefined, event )
                      }
                      else if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onFieldBlur();
                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onLinkButtonClick                 = useCallback(
                  () => {
                      const link = document.createElement( 'a' );

                      link.setAttribute( 'href', `tel:${outValue.current}` );
                      link.setAttribute( 'target', '_blank' );
                      link.click();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputKeyDownCapture             = useCallback(
                  ( event : React.KeyboardEvent<HTMLInputElement> ) => {

                      const selectionStart = ( document.getElementById( elementId ) as unknown as HTMLInputElement | undefined )?.selectionStart ?? null,
                            selectionEnd   = ( document.getElementById( elementId ) as unknown as HTMLInputElement | undefined )?.selectionEnd ?? null,
                            currentValue   = ( document.getElementById( elementId ) as unknown as HTMLInputElement | undefined )?.value;

                      // special handling of cases where input leads to an unchanged element value -> inputChange won't work as expected
                      // 1. replace defaultCountryCode by 0
                      // 2. replace a phone number by exactly the same via paste
                      if (
                          ( ( outValue.current === `+${defaultCountryCode}` && event.key === '0' ) || ( event.key === 'v' && event.metaKey ) )
                          && selectionStart === 0 && selectionEnd === ( currentValue?.replaceAll( '​', '' ).length ?? 0 - 1 )
                      ) {
                          setElementValue( '' );
                      }

                      if ( event.key.match( /^[0-9]$/ ) ) {

                          if ( selectionStart !== null ) {
                              //cursorPosition.current = selectionStart + 1;
                          }
                          else {
                              event.preventDefault();
                              event.stopPropagation();
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onKeyUpCapture                    = useCallback(
                  () => {
                      if ( cursorPosition.current !== -1 ) {
                          ( inputRef.current as unknown as HTMLInputElement )?.setSelectionRange?.( cursorPosition.current, cursorPosition.current );
                          cursorPosition.current = -1;
                      }

                      fixCursorPosition();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              getInput                          = () => {
                  let retVal = <InputMask
                      {..._.pick( props, inputProps ) as InputProps}

                      id={elementId}

                      ref={( node ) => {
                          inputRef.current = node;
                          if ( typeof forwardedRef === 'function' ) {
                              forwardedRef( node );
                          }
                          else if ( forwardedRef ) {
                              forwardedRef.current = node;
                          }
                      }}

                      value={elementValue ?? ''}

                      onChange={useHookForm ? onHookFormInputChange : onInputChange}

                      pt={{
                          root: {
                              onFocus         : onInputFocus,
                              onBlur          : onInputBlur,
                              onKeyDownCapture: onInputKeyDownCapture,
                              onKeyUpCapture  : onKeyUpCapture,
                              onMouseUpCapture: fixCursorPosition
                          }
                      }}

                      mask={mask}
                      unmask={true}
                      slotChar={'\u200b'}
                      autoClear={false}

                      className={`yn-phonefield ${inputClassNames.join( ' ' )} ${rawMode ? 'yn-rawmode' : ''}`}
                      style={{ ...defaultProps.inputStyle, ...inputStyle }}

                      placeholder={resolvedPlaceholder}
                      tooltip={undefined}
                      tooltipOptions={undefined}

                      aria-describedby={`${elementId}_message`}

                      autoComplete={autoComplete && !disabled ? 'off' : 'on'}
                      aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                      aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                      aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
                  />;

                  if ( linkButton ) {
                      retVal = <div id={`${elementId}_wrapper`} className={`p-inputgroup yn-inputgroup yn-subfield${disabled ? ' p-disabled yn-enabled-button' : ''}`}>
                          {retVal}
                          <Button
                              icon="pi pi-phone"
                              className={( elementValue ?? '' ) !== '' ? 'yn-enabled' : ''}
                              disabled={( elementValue ?? '' ) === ''}

                              onClick={onLinkButtonClick}
                              onFocus={onFieldFocus}
                              onBlur={onFieldBlur}

                              tabIndex={tabIndex ? tabIndex + 10 : 0}
                          />
                      </div>;
                  }

                  return retVal;
              };

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            displayValueWidthOffsetRem={linkButton ? 2.357 : undefined}
            displayValueWidthOffsetPx={linkButton ? 1 : undefined}

            {...fieldWrapperAutoCompleteConfig}
            pt={pt?.fieldWrapper}
        >
            {getInput()}
        </FieldWrapper>;
    }
} );
PhoneField.defaultProps = defaultProps;
PhoneField.displayName = 'PhoneField';

export default PhoneField;
